import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { bottomSheetDockClassName } from "@/lib/bottom-sheet-compact";
import { Plus } from "lucide-react";

interface FabAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface FabMenuProps {
  actions: FabAction[];
  className?: string;
  /** Optional icon for the floating button (default: Plus). */
  fabIcon?: React.ReactNode;
  /** Overrides FAB `aria-label` (default: common.quickActions). */
  fabAriaLabel?: string;
  /**
   * If true, FAB is hidden (e.g., during form edit mode)
   */
  hidden?: boolean;
  /** If true, always open the action sheet even when there is only one action. */
  alwaysShowMenu?: boolean;
}

/**
 * FAB Menu - Floating Action Button with action sheet menu (Bigin-style)
 * 
 * Features:
 * - Fixed bottom-right position (above bottom nav)
 * - Opens action sheet on tap
 * - Customizable actions (Files, Call, Event, Task, Notes, etc.)
 * - Safe-area aware (iOS notch)
 * - Touch-optimized (56px FAB, 48px+ menu items)
 * 
 * Usage:
 * ```tsx
 * <FabMenu
 *   actions={[
 *     { id: 'files', label: 'Upload Files', icon: <Upload />, onClick: handleUpload },
 *     { id: 'call', label: 'Log Call', icon: <Phone />, onClick: handleCall },
 *     { id: 'event', label: 'Add Event', icon: <Calendar />, onClick: handleEvent },
 *     { id: 'task', label: 'Create Task', icon: <CheckSquare />, onClick: handleTask },
 *     { id: 'note', label: 'Add Note', icon: <FileText />, onClick: handleNote },
 *   ]}
 * />
 * ```
 */
/* fab-menu: canonical bottom/right + safe-area (Phase D) */
export function FabMenu({
  actions,
  className,
  fabIcon,
  fabAriaLabel,
  hidden = false,
  alwaysShowMenu = false,
}: FabMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const { t } = useTranslation();

  if (hidden || actions.length === 0) return null;

  const single = !alwaysShowMenu && actions.length === 1 ? actions[0] : null;
  const fabSurfaceIcon = single ? (fabIcon ?? single.icon) : (fabIcon ?? <Plus className="h-6 w-6" />);
  const fabButtonAria = single ? (fabAriaLabel ?? single.label) : (fabAriaLabel ?? t("common.quickActions"));

  const handleActionClick = (action: FabAction) => {
    setShowMenu(false);
    action.onClick();
  };

  const handleFabClick = () => {
    if (single) {
      single.onClick();
      return;
    }
    setShowMenu(true);
  };

  return (
    <>
      {/* FAB Button */}
      <button
        type="button"
        onClick={handleFabClick}
        className={cn(
          "fixed z-40",
          "h-14 w-14 rounded-full",
          "bg-primary text-primary-foreground",
          "shadow-lg hover:shadow-xl",
          "flex items-center justify-center",
          "transition-all duration-200",
          "active:scale-95",
          className
        )}
        style={{
          // Canonical FAB position: 1rem above bottom nav + safe-area
          // (offset is configurable via CSS variable for future tweaks)
          bottom: "calc(4rem + env(safe-area-inset-bottom) + var(--fab-bottom-offset, 1rem))",
          right: "max(1rem, env(safe-area-inset-right))",
        }}
        aria-label={fabButtonAria}
      >
        {fabSurfaceIcon}
      </button>

      {/* Action Sheet (skipped when single action runs directly from FAB) */}
      <Sheet open={showMenu} onOpenChange={setShowMenu}>
        <SheetContent side="bottom" className={bottomSheetDockClassName()}>
          {/* Drag handle — minimal top breathing room */}
          <div className="mx-auto mb-2 h-1 w-10 shrink-0 rounded-full bg-muted" aria-hidden />

          <SheetHeader className="max-[767px]:pt-0 space-y-0 px-0 pb-2 pt-0 text-left">
            <SheetTitle className="text-base font-semibold leading-snug">
              {t("common.quickActions")}
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-0.5 pb-3">
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => handleActionClick(action)}
                className={cn(
                  "flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
                  "transition-colors hover:bg-accent hover:text-accent-foreground",
                  "text-foreground"
                )}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center text-primary">
                  {action.icon}
                </div>
                <span className="text-sm font-medium leading-snug">{action.label}</span>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
